// The your-turn nudge (issue 1050, COMM-005). The on-board notice already tells a player who is looking; this
// reaches one who is not: while the tab is hidden the title flashes "Your turn — <name>" until they come back, and,
// if they opted in once with a click, the browser shows a notification. It is computed only from this viewer's own
// projected encounter (`turnCame`: their `mine` combatant becoming active), so it reaches the active player only,
// by construction — and nothing is sent to anyone: the Notification API is local to this browser.
import { useState } from 'react';

interface EncLike { active?: string | null; combatants?: Array<{ id: string; mine: boolean; name: string }>; prompt?: Array<{ id: string; name: string }> }

/**
 * The names newly in this viewer's own call for initiative (qa-runs 405): the DM's Roll monsters prompting a combatant
 * of theirs. Read only from their own projection, which carries only their own prompt; null when nothing new.
 */
export function promptCame(was: EncLike | undefined, now: EncLike | undefined): string[] | null {
  const had = new Set((was?.prompt ?? []).map((p) => p.id));
  const fresh = (now?.prompt ?? []).filter((p) => !had.has(p.id)).map((p) => p.name);
  return fresh.length ? fresh : null;
}

/** The name of this viewer's own combatant that has just become active, or null. */
export function turnCame(was: EncLike | undefined, now: EncLike | undefined): string | null {
  if (!now?.active || now.active === was?.active) return null;
  const c = now.combatants?.find((x) => x.id === now.active);
  return c?.mine ? c.name : null;
}

// The opt-in lives in this browser, where the permission itself lives; a per-viewer convenience, not table state.
const KEY = 'vtt.turnNotify';
const optedIn = (): boolean => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };
const setOptedIn = (on: boolean): void => { try { if (on) localStorage.setItem(KEY, '1'); else localStorage.removeItem(KEY); } catch { /* storage refused: off */ } };
const api = (): typeof Notification | null => (typeof Notification === 'undefined' ? null : Notification);

let flashing: { original: string; timer: ReturnType<typeof setInterval>; off: () => void } | null = null;

/** Stop flashing and put the title back. */
export function stopNudge(): void {
  if (!flashing) return;
  clearInterval(flashing.timer);
  flashing.off();
  document.title = flashing.original;
  flashing = null;
}

/** Nudge this player that their turn has come. Does nothing while the tab is in view: the on-board notice says it. */
export function nudgeTurn(name: string): void {
  nudge(`Your turn — ${name}`, 'It\'s your turn', name);
}

/** Nudge this player that the DM calls for their initiative (qa-runs 405), by the same opt-in as their turn. */
export function nudgeInitiative(names: string[]): void {
  nudge(`Roll initiative — ${names.join(', ')}`, 'Roll initiative', names.join(', '));
}

function nudge(text: string, title: string, body: string): void {
  if (document.visibilityState !== 'hidden') return;
  const original = flashing?.original ?? document.title;
  stopNudge();
  document.title = text;
  let lit = true;
  const timer = setInterval(() => { lit = !lit; document.title = lit ? text : `• ${text}`; }, 1000);
  const back = () => { if (document.visibilityState !== 'hidden') stopNudge(); };
  document.addEventListener('visibilitychange', back);
  window.addEventListener('focus', back);
  flashing = { original, timer, off: () => { document.removeEventListener('visibilitychange', back); window.removeEventListener('focus', back); } };
  const N = api();
  if (optedIn() && N && N.permission === 'granted') {
    try { new N(title, { body, tag: 'vtt-turn' }); } catch { /* a browser that refuses: the title still flashes */ }
  }
}

/** The opt-in, once, by a click: the browser is asked for permission only here, never on load. */
export function TurnNotifyToggle() {
  const N = api();
  const [on, setOn] = useState(() => optedIn() && N?.permission !== 'denied');
  if (!N) return null;
  const toggle = async () => {
    if (on) { setOptedIn(false); setOn(false); return; }
    const answer = N.permission === 'granted' ? 'granted' : await N.requestPermission();
    const yes = answer === 'granted';
    setOptedIn(yes); setOn(yes);
  };
  return (
    <label className="flex items-center gap-1 text-xs text-stone-400" title="While this tab is hidden, your browser shows a notification when your turn comes. Nothing is sent to anyone else.">
      <input type="checkbox" aria-label="Notify me when it's my turn" checked={on} onChange={() => { void toggle(); }} /> Notify me when it&apos;s my turn
      {N.permission === 'denied' && <span className="text-stone-500">(blocked in this browser)</span>}
    </label>
  );
}
